import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Files, UploadSimple, Truck, User, Buildings, ArrowClockwise, Warning,
  X as XIcon, DownloadSimple, Trash, MagnifyingGlass, Calendar, ArrowRight,
  CheckCircle, Package, PencilSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";

const TABS = [
  { key: "driver",  label: "Drivers",   icon: User },
  { key: "truck",   label: "Trucks",    icon: Truck },
  { key: "load",    label: "Loads",     icon: Package },
  { key: "company", label: "Company",   icon: Buildings },
];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const exp = new Date(dateStr);
    const now = new Date();
    exp.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return Math.round((exp - now) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function ExpirationBadge({ dateStr }) {
  const days = daysUntil(dateStr);
  if (days === null) return <span className="text-[10px] text-neutral-600">no expiration</span>;
  if (days < 0) return (
    <span className="text-[10px] uppercase tracking-widest text-red-400 font-semibold inline-flex items-center gap-1">
      <Warning size={10} weight="fill" /> expired {Math.abs(days)}d ago
    </span>
  );
  if (days <= 7) return (
    <span className="text-[10px] uppercase tracking-widest text-red-400 font-semibold">
      expires in {days}d
    </span>
  );
  if (days <= 30) return (
    <span className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">
      expires in {days}d
    </span>
  );
  return (
    <span className="text-[10px] text-neutral-500 tabular-nums">
      exp {new Date(dateStr).toLocaleDateString()} · {days}d left
    </span>
  );
}

function UploadModal({ open, onClose, entity, categories, onUploaded }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null); setCategory(""); setExpirationDate(""); setNotes("");
    }
  }, [open]);

  if (!open || !entity) return null;

  const submit = async () => {
    if (!file) { toast.error("Pick a file"); return; }
    if (!category) { toast.error("Pick a category"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", entity.entity_type);
    fd.append("entity_id", entity.entity_id);
    fd.append("category", category);
    if (expirationDate) fd.append("expiration_date", expirationDate);
    if (notes) fd.append("notes", notes);
    try {
      await client.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${category} for ${entity.display_name}`);
      onUploaded?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         onClick={(e) => e.stopPropagation()}
         data-testid="upload-doc-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
                Upload document
              </div>
              <div className="text-sm font-medium text-white">{entity.display_name}</div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white p-1"
                    data-testid="upload-doc-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* File */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">File</div>
              <label className="block cursor-pointer rounded-lg border border-dashed border-white/20 hover:border-[#FF5A1F]/50 p-6 text-center transition-colors"
                     data-testid="upload-doc-drop">
                <UploadSimple size={20} className="mx-auto text-neutral-500 mb-2" />
                {file ? (
                  <>
                    <div className="text-sm text-white font-medium">{file.name}</div>
                    <div className="text-[11px] text-neutral-500">
                      {(file.size / 1024).toFixed(1)} KB · {file.type || "unknown type"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-neutral-300">Click to pick a PDF, image, or Office doc</div>
                    <div className="text-[10px] text-neutral-600 mt-1">Max 15 MB</div>
                  </>
                )}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt"
                  className="hidden"
                  data-testid="upload-doc-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            {/* Category */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">
                Category
              </div>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      data-testid="upload-doc-category"
                      className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none">
                <option value="">— Pick a category —</option>
                {(categories?.[entity.entity_type] || []).map((c) => (
                  <option key={c} value={c} className="bg-black">{c}</option>
                ))}
              </select>
            </div>

            {/* Expiration */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">
                Expiration date <span className="text-neutral-600">(optional but recommended)</span>
              </div>
              <input type="date" value={expirationDate}
                     onChange={(e) => setExpirationDate(e.target.value)}
                     data-testid="upload-doc-expiration"
                     className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none" />
            </div>

            {/* Notes */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">
                Notes <span className="text-neutral-600">(optional)</span>
              </div>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                        rows={2} placeholder="Any relevant details…"
                        data-testid="upload-doc-notes"
                        className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none" />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={onClose} disabled={uploading}
                className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-400 hover:text-white">
                Cancel
              </button>
              <button onClick={submit} disabled={uploading || !file || !category}
                data-testid="upload-doc-submit"
                className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60">
                {uploading ? <><ArrowClockwise size={12} className="animate-spin" /> Uploading…</> :
                  <><UploadSimple size={12} weight="bold" /> Upload document</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EntityDocsDrawer({ entity, categories, onClose, onChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);

  const load = async () => {
    if (!entity) return;
    setLoading(true);
    try {
      const { data } = await client.get(
        `/documents?entity_type=${entity.entity_type}&entity_id=${entity.entity_id}`);
      setDocs(data.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load docs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity?.entity_id]);

  if (!entity) return null;

  const downloadOne = async (doc) => {
    try {
      const r = await client.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `${doc.category}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadBundle = async () => {
    setDownloadingBundle(true);
    try {
      const r = await client.get(
        `/documents/${entity.entity_type}/${entity.entity_id}/bundle.zip`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity.display_name.replace(/\s+/g, "_")}_docs.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bundle failed");
    } finally {
      setDownloadingBundle(false);
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename || doc.category}? This is undoable — you'll need to re-upload.`)) return;
    try {
      await client.delete(`/documents/${doc.id}`);
      toast.success("Deleted");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
         onClick={onClose} data-testid="entity-drawer">
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-[#0A0A0A] border-l border-white/10 overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
              {entity.entity_type}
            </div>
            <div className="text-lg font-display font-semibold text-white">{entity.display_name}</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {docs.length} document{docs.length !== 1 ? "s" : ""}
              {entity.expired > 0 && <span className="text-red-400 ml-2">· {entity.expired} expired</span>}
              {entity.expiring_soon > 0 && <span className="text-amber-400 ml-2">· {entity.expiring_soon} expiring soon</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {docs.length > 0 && (
              <button onClick={downloadBundle} disabled={downloadingBundle}
                data-testid="entity-bundle-btn"
                className="rounded-full px-3 py-1.5 text-xs border border-white/15 hover:border-white/30 text-neutral-300 hover:text-white inline-flex items-center gap-1.5">
                {downloadingBundle ? <><ArrowClockwise size={11} className="animate-spin" /> Zipping…</> :
                  <><Package size={11} /> Download all (ZIP)</>}
              </button>
            )}
            <button onClick={() => setUploadOpen(true)}
              data-testid="entity-upload-btn"
              className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5">
              <UploadSimple size={11} weight="bold" /> Upload
            </button>
            <button onClick={onClose} className="text-neutral-500 hover:text-white p-1">
              <XIcon size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          {loading ? (
            <div className="text-sm text-neutral-500 py-12 text-center">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 py-12 text-center">
              <Files size={20} className="mx-auto mb-2 text-neutral-600" />
              <div className="text-sm text-neutral-500">No documents yet.</div>
              <div className="text-[11px] text-neutral-600 mt-1">
                {entity.entity_type === "load"
                  ? "Click Upload to add the Rate Confirmation, BOL, POD, or any lumper/detention receipts for this trip."
                  : "Click Upload to add CDL, med card, insurance, etc."}
              </div>
            </div>
          ) : (
            docs.map((d) => (
              <div key={d.id}
                   className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
                   data-testid={`doc-row-${d.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white truncate">{d.category}</span>
                      <ExpirationBadge dateStr={d.expiration_date} />
                    </div>
                    <div className="text-[11px] text-neutral-500 font-mono truncate mt-0.5">{d.filename}</div>
                    {d.notes && <div className="text-[11px] text-neutral-400 mt-1 italic">"{d.notes}"</div>}
                    <div className="text-[10px] text-neutral-600 mt-1">
                      {(d.size_bytes / 1024).toFixed(1)} KB · {d.content_type} · uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => downloadOne(d)}
                      data-testid={`doc-download-${d.id}`}
                      className="p-2 text-neutral-400 hover:text-white rounded hover:bg-white/[0.05]"
                      title="Download">
                      <DownloadSimple size={14} />
                    </button>
                    <button onClick={() => deleteDoc(d)}
                      data-testid={`doc-delete-${d.id}`}
                      className="p-2 text-neutral-500 hover:text-red-400 rounded hover:bg-red-500/10"
                      title="Delete">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        entity={entity}
        categories={categories}
        onUploaded={() => { load(); onChange?.(); }}
      />
    </div>
  );
}

export default function PaperworkPage() {
  const [tab, setTab] = useState("driver");
  const [entities, setEntities] = useState({ drivers: [], trucks: [], loads: [], company: null });
  const [categories, setCategories] = useState({});
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, ex] = await Promise.all([
        client.get("/documents/entities"),
        client.get("/documents/expiring?days=30"),
      ]);
      setEntities({
        drivers: e.data.drivers || [],
        trucks: e.data.trucks || [],
        loads: e.data.loads || [],
        company: e.data.company,
      });
      setCategories(e.data.categories || {});
      setExpiring(ex.data.documents || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load paperwork");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    let list = tab === "driver" ? entities.drivers :
               tab === "truck"  ? entities.trucks :
               tab === "load"   ? entities.loads :
               entities.company ? [entities.company] : [];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => {
        const name = (r.display_name || "").toLowerCase();
        const cust = (r.meta?.customer || "").toLowerCase();
        return name.includes(q) || cust.includes(q);
      });
    }
    return list;
  }, [tab, entities, search]);

  const alertCount = expiring.length;

  return (
    <div className="space-y-6" data-testid="paperwork-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">PAPERWORK</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Documents for drivers, trucks, loads, and company
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            One place to store CDLs, medical cards, insurance COIs, IFTA, DOT inspections, and every
            rate con, BOL, and POD your brokers ask for before they pay. Track expirations, download
            bulk ZIPs, never hunt through emails again.
          </p>
        </div>
      </div>

      {/* Expiration alert banner */}
      {alertCount > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-4"
             data-testid="expiring-alert">
          <div className="flex items-start gap-3">
            <Warning size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-200">
                {alertCount} document{alertCount !== 1 ? "s" : ""} expiring in the next 30 days (or already expired)
              </div>
              <div className="mt-2 space-y-1">
                {expiring.slice(0, 5).map((d) => (
                  <div key={d.id} className="text-xs text-amber-100/80 inline-flex items-center gap-2 mr-4"
                       data-testid={`expiring-row-${d.id}`}>
                    <span className="font-medium">{d.entity_display}</span>
                    <ArrowRight size={10} className="text-amber-400/50" />
                    <span>{d.category}</span>
                    <ExpirationBadge dateStr={d.expiration_date} />
                  </div>
                ))}
                {alertCount > 5 && (
                  <div className="text-[10px] text-amber-100/60 mt-1">+ {alertCount - 5} more</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = t.key === "driver" ? entities.drivers.length :
                        t.key === "truck"  ? entities.trucks.length :
                        t.key === "load"   ? entities.loads.length : 1;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`paperwork-tab-${t.key}`}
              className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                tab === t.key
                  ? "border-[#FF5A1F] text-white"
                  : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
            >
              <Icon size={13} weight="regular" />
              {t.label} <span className="text-neutral-600">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Search (drivers + trucks only) */}
      {tab !== "company" && (
        <div className="relative max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              tab === "driver" ? "Search drivers…" :
              tab === "truck"  ? "Search trucks…" :
              "Search loads or customers…"
            }
            data-testid="paperwork-search"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-black/40 border border-white/10 text-white text-sm focus:border-[#FF5A1F] outline-none"
          />
        </div>
      )}

      {/* Entity grid */}
      {loading ? (
        <div className="text-sm text-neutral-500 py-16 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 py-12 text-center text-sm text-neutral-500">
          {tab === "driver" ? "No drivers yet — add some on the Drivers page first." :
           tab === "truck"  ? "No trucks yet — add some on the Fleet page first." :
           tab === "load"   ? "No open loads yet — add one on the Loads page and its BOL/POD paperwork will live here." :
                              "Company info not available."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="entity-grid">
          {rows.map((r) => (
            <button
              key={`${r.entity_type}-${r.entity_id}`}
              onClick={() => setSelectedEntity(r)}
              data-testid={`entity-card-${r.entity_id}`}
              className={`text-left rounded-lg border p-4 transition-colors ${
                r.expired > 0 ? "border-red-500/30 bg-red-500/[0.03] hover:bg-red-500/[0.06]" :
                r.expiring_soon > 0 ? "border-amber-500/25 bg-amber-500/[0.03] hover:bg-amber-500/[0.05]" :
                "border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{r.display_name}</div>
                  {r.meta?.phone && (
                    <div className="text-[11px] text-neutral-500 font-mono truncate">{r.meta.phone}</div>
                  )}
                  {r.meta?.vin && (
                    <div className="text-[10px] text-neutral-500 font-mono truncate">VIN: {r.meta.vin}</div>
                  )}
                  {r.entity_type === "load" && r.meta?.customer && (
                    <div className="text-[11px] text-neutral-500 truncate mt-0.5">
                      <span className="text-neutral-600">Customer:</span> {r.meta.customer}
                    </div>
                  )}
                  {r.entity_type === "load" && r.meta?.delivery_date && (
                    <div className="text-[10px] text-neutral-600 truncate mt-0.5">
                      Delivered {r.meta.delivery_date}
                      {r.meta.status && (
                        <span className="ml-2 uppercase tracking-wider text-[9px]">· {r.meta.status}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  <Files size={16} className="text-neutral-500" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 text-[11px]">
                <span className="text-white tabular-nums">
                  {r.total} <span className="text-neutral-500">doc{r.total !== 1 ? "s" : ""}</span>
                </span>
                {r.expired > 0 && (
                  <span className="text-red-400 tabular-nums inline-flex items-center gap-1">
                    <Warning size={10} weight="fill" /> {r.expired} expired
                  </span>
                )}
                {r.expiring_soon > 0 && (
                  <span className="text-amber-400 tabular-nums inline-flex items-center gap-1">
                    <Calendar size={10} weight="fill" /> {r.expiring_soon} soon
                  </span>
                )}
                {r.total > 0 && r.expired === 0 && r.expiring_soon === 0 && (
                  <span className="text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle size={10} weight="fill" /> all current
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedEntity && (
        <EntityDocsDrawer
          entity={selectedEntity}
          categories={categories}
          onClose={() => setSelectedEntity(null)}
          onChange={load}
        />
      )}
    </div>
  );
}
